import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Eye, Heart, MessageCircle, Star, RefreshCw } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const StatPill = ({ label, value, testId }) => (
  <div
    data-testid={testId}
    className="bg-zinc-900/60 border border-red-900/40 rounded-lg px-5 py-4"
  >
    <p className="text-gray-400 text-xs uppercase tracking-widest mb-1">{label}</p>
    <p className="text-3xl font-black text-white">{Number(value).toLocaleString()}</p>
  </div>
);

const formatDate = (iso) => {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleDateString();
  } catch {
    return iso;
  }
};

const SORT_OPTIONS = [
  { id: 'views', label: 'Most Views' },
  { id: 'unique_views', label: 'Most Unique' },
  { id: 'likes', label: 'Most Liked' },
  { id: 'comments', label: 'Most Commented' },
  { id: 'uploaded_at', label: 'Newest First' },
];

const VideoViewsTab = ({ token }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sortBy, setSortBy] = useState('views');

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/admin/video-stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to load video stats');
      const json = await res.json();
      setData(json);
    } catch (e) {
      setError(e.message || 'Failed to load video stats');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const sortedVideos = useMemo(() => {
    if (!data) return [];
    const arr = [...data.videos];
    arr.sort((a, b) => {
      if (sortBy === 'uploaded_at') {
        return (b.uploaded_at || '').localeCompare(a.uploaded_at || '');
      }
      return (b[sortBy] || 0) - (a[sortBy] || 0);
    });
    return arr;
  }, [data, sortBy]);

  if (loading) {
    return <div className="text-gray-400" data-testid="video-stats-loading">Loading video stats...</div>;
  }
  if (error) {
    return (
      <div
        className="bg-red-900/20 border border-red-500/50 text-red-400 px-4 py-3 rounded"
        data-testid="video-stats-error"
      >
        {error}
      </div>
    );
  }
  if (!data) return null;

  const maxViews = sortedVideos.reduce((m, v) => Math.max(m, v.views || 0), 0);

  return (
    <div className="space-y-6" data-testid="video-views-tab">
      {/* Top totals */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <StatPill label="Videos" value={data.totals.videos} testId="vstat-videos" />
        <StatPill label="Total Views" value={data.totals.views} testId="vstat-views" />
        <StatPill label="Unique Viewers" value={data.totals.unique_views} testId="vstat-unique" />
        <StatPill label="Likes" value={data.totals.likes} testId="vstat-likes" />
        <StatPill label="Comments" value={data.totals.comments} testId="vstat-comments" />
      </div>

      {/* Controls */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-gray-500 uppercase tracking-widest mr-1">Sort:</span>
          {SORT_OPTIONS.map((opt) => (
            <button
              key={opt.id}
              onClick={() => setSortBy(opt.id)}
              data-testid={`vstats-sort-${opt.id}`}
              className={`px-3 py-1.5 rounded text-xs font-bold transition-colors ${
                sortBy === opt.id
                  ? 'bg-red-600 text-white'
                  : 'bg-zinc-900 text-gray-400 hover:text-white border border-red-900/30'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <button
          onClick={fetchStats}
          data-testid="vstats-refresh-btn"
          className="flex items-center gap-1.5 bg-zinc-900 hover:bg-zinc-800 border border-red-900/30 text-gray-300 hover:text-white font-bold px-3 py-1.5 rounded text-xs transition-colors"
        >
          <RefreshCw className="h-3.5 w-3.5" />
          Refresh
        </button>
      </div>

      {/* Video leaderboard */}
      {sortedVideos.length === 0 ? (
        <div className="bg-zinc-900/60 border border-red-900/30 rounded-lg p-8 text-center text-gray-400" data-testid="vstats-empty">
          No videos uploaded yet. Upload your first video from the <span className="text-red-400 font-bold">18+ Videos</span> tab.
        </div>
      ) : (
        <div className="bg-zinc-900/80 backdrop-blur-xl border border-red-900/30 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-400 uppercase text-xs tracking-wider border-b border-red-900/30">
                <th className="px-4 py-3">#</th>
                <th className="px-4 py-3">Video</th>
                <th className="px-4 py-3 text-right">Views</th>
                <th className="px-4 py-3 text-right hidden sm:table-cell">Unique</th>
                <th className="px-4 py-3 text-right">Likes</th>
                <th className="px-4 py-3 text-right">Comments</th>
                <th className="px-4 py-3 text-right hidden md:table-cell">Uploaded</th>
              </tr>
            </thead>
            <tbody data-testid="vstats-list">
              {sortedVideos.map((v, idx) => {
                const widthPct = maxViews ? Math.max(2, (v.views / maxViews) * 100) : 0;
                return (
                  <tr
                    key={v.id}
                    data-testid={`vstats-row-${v.id}`}
                    className="border-b border-red-900/10 last:border-0 hover:bg-zinc-950/50"
                  >
                    <td className="px-4 py-3 text-red-400 font-black tabular-nums">{idx + 1}</td>
                    <td className="px-4 py-3 min-w-0">
                      <div className="flex items-center gap-2 min-w-0 mb-1">
                        {v.is_exclusive && (
                          <Star className="h-3.5 w-3.5 text-yellow-400 fill-yellow-400 shrink-0" />
                        )}
                        <span className="text-white font-bold truncate" title={v.title}>
                          {v.title}
                        </span>
                      </div>
                      <div className="h-1.5 w-full bg-zinc-800/80 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-red-700 to-red-500"
                          style={{ width: `${widthPct}%` }}
                        />
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-1 text-white font-black tabular-nums">
                        <Eye className="h-3.5 w-3.5 text-gray-400" />
                        {v.views.toLocaleString()}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right text-gray-300 tabular-nums hidden sm:table-cell">
                      {v.unique_views.toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-1 text-red-400 font-bold tabular-nums">
                        <Heart className="h-3.5 w-3.5" />
                        {v.likes.toLocaleString()}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-1 text-gray-300 font-bold tabular-nums">
                        <MessageCircle className="h-3.5 w-3.5" />
                        {v.comments.toLocaleString()}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right text-gray-500 text-xs hidden md:table-cell whitespace-nowrap">
                      {formatDate(v.uploaded_at)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default VideoViewsTab;
